import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AppDialog from "components/common/AppDialog";
import {
  BackendUserAutocompleteListbox,
  backendUserAutocompleteComponentsProps,
  renderBackendUserAutocompleteOption,
} from "components/common/BackendUserLabelBlock";
import { useBackendUsersAutocompleteInfinite } from "features/adminUsers/useBackendUsersAutocompleteInfinite";
import { formatBackendUserOptionLabel } from "utils/backendUserLabel";
import type { BackendUser } from "types/interfaces/adminUsers";
import {
  useCreatePdfJobMutation,
  useFinalizePdfUploadMutation,
  useGetPdfToolsConfigQuery,
  usePresignPdfUploadMutation,
  type PdfOptionSchemaProperty,
  type PdfSplitterInfo,
} from "features/pdfTools/pdfTools";
import { trackPdfSplitJob } from "features/pdfSplitJobsSlice";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { formatApiError } from "utils/formatApiError";
import { formatBytes } from "components/preview/previewTypes";
import PdfFilesDropzone, { type UploadItem } from "./PdfFilesDropzone";

interface NewSplitJobDialogProps {
  open: boolean;
  onClose: () => void;
}

const ALLOWED_EXTENSIONS = [".zip", ".pdf"];

function fileExtension(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx).toLowerCase() : "";
}

function schemaDefaults(splitter: PdfSplitterInfo | null): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  const properties = splitter?.options_schema?.properties ?? {};
  for (const [name, property] of Object.entries(properties)) {
    if (property.default !== undefined) defaults[name] = property.default;
    else if (property.type === "boolean") defaults[name] = false;
    else defaults[name] = "";
  }
  return defaults;
}

export default function NewSplitJobDialog({
  open,
  onClose,
}: NewSplitJobDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: config, isLoading: configLoading } = useGetPdfToolsConfigQuery(
    undefined,
    { skip: !open },
  );
  const [presignUpload] = usePresignPdfUploadMutation();
  const [finalizeUpload] = useFinalizePdfUploadMutation();
  const [createJob] = useCreatePdfJobMutation();

  const [selectedUser, setSelectedUser] = useState<BackendUser | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [splitterKey, setSplitterKey] = useState("");
  const [optionValues, setOptionValues] = useState<Record<string, unknown>>({});
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [items, setItems] = useState<UploadItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [formError, setFormError] = useState("");

  const {
    options: userOptions,
    loading: usersLoading,
    onListboxScroll: usersListboxOnScroll,
  } = useBackendUsersAutocompleteInfinite({
    skip: !open,
    page_size: 50,
    search: userSearch,
    value: selectedUser,
  });

  const splitters = useMemo(() => config?.splitters ?? [], [config]);
  const splitter = splitters.find((s) => s.key === splitterKey) ?? null;
  const schemaProperties = useMemo(
    () =>
      Object.entries(splitter?.options_schema?.properties ?? {}) as [
        string,
        PdfOptionSchemaProperty,
      ][],
    [splitter],
  );
  const requiredOptions = splitter?.options_schema?.required ?? [];
  const limits = config?.limits;

  const resetForm = () => {
    setSelectedUser(null);
    setUserSearch("");
    setSplitterKey("");
    setOptionValues({});
    setShowSecret({});
    setItems([]);
    setSubmitting(false);
    setUploadedCount(0);
    setFormError("");
  };

  useEffect(() => {
    if (!submitting) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [submitting]);

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const handleSplitterChange = (key: string) => {
    setSplitterKey(key);
    const next = splitters.find((s) => s.key === key) ?? null;
    setOptionValues(schemaDefaults(next));
  };

  const setOption = (name: string, value: unknown) => {
    setOptionValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFiles = (files: File[]) => {
    setFormError("");
    setItems((prev) => {
      const next = [...prev];
      for (const file of files) {
        const ext = fileExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          dispatch(
            pushNotification({
              message: `"${file.name}" is not a ZIP or PDF file.`,
              severity: "warning",
            }),
          );
          continue;
        }
        if (file.size === 0) {
          dispatch(
            pushNotification({
              message: `"${file.name}" is empty.`,
              severity: "warning",
            }),
          );
          continue;
        }
        if (limits && file.size > limits.max_file_bytes) {
          dispatch(
            pushNotification({
              message: `"${file.name}" exceeds the ${formatBytes(limits.max_file_bytes)} per-file limit.`,
              severity: "warning",
            }),
          );
          continue;
        }
        if (next.some((i) => i.file.name === file.name)) {
          dispatch(
            pushNotification({
              message: `"${file.name}" is already in the list.`,
              severity: "warning",
            }),
          );
          continue;
        }
        if (limits && next.length >= limits.max_files_per_job) {
          dispatch(
            pushNotification({
              message: `At most ${limits.max_files_per_job} files are allowed per job.`,
              severity: "warning",
            }),
          );
          break;
        }
        next.push({ file, status: "queued" });
      }
      return next;
    });
  };

  const handleRemoveFile = (name: string) => {
    setItems((prev) => prev.filter((i) => i.file.name !== name));
  };

  const setItemState = (name: string, patch: Partial<UploadItem>) => {
    setItems((prev) =>
      prev.map((i) => (i.file.name === name ? { ...i, ...patch } : i)),
    );
  };

  const requiredFilled = requiredOptions.every((name) => {
    const value = optionValues[name];
    return typeof value === "string" ? value.trim().length > 0 : value != null;
  });

  const canSubmit =
    !submitting && !!selectedUser && !!splitter && requiredFilled && items.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedUser || !splitter) return;
    setSubmitting(true);
    setFormError("");
    setUploadedCount(items.filter((i) => i.status === "uploaded").length);

    try {
      const uploaded: { s3_key: string; file_name: string }[] = [];
      for (const item of items) {
        if (item.status === "uploaded" && item.s3Key) {
          uploaded.push({ s3_key: item.s3Key, file_name: item.file.name });
          continue;
        }
        setItemState(item.file.name, { status: "uploading", error: undefined });
        try {
          const presigned = await presignUpload({
            file_name: item.file.name,
            content_type: item.file.type || "application/octet-stream",
            user_id: selectedUser.id,
          }).unwrap();
          const putRes = await fetch(presigned.upload_url, {
            method: "PUT",
            headers: presigned.headers,
            body: item.file,
          });
          if (!putRes.ok) {
            throw new Error(`Upload failed (${putRes.status})`);
          }
          const finalized = await finalizeUpload({
            s3_key: presigned.s3_key,
            user_id: selectedUser.id,
          }).unwrap();
          setItemState(item.file.name, {
            status: "uploaded",
            s3Key: finalized.s3_key,
          });
          uploaded.push({
            s3_key: finalized.s3_key,
            file_name: item.file.name,
          });
          setUploadedCount((n) => n + 1);
        } catch (err: unknown) {
          const message =
            (err as { data?: unknown })?.data !== undefined
              ? formatApiError((err as { data?: unknown }).data, "Upload failed.")
              : "Upload failed — check your connection and try again.";
          setItemState(item.file.name, { status: "failed", error: message });
          setFormError(`Could not upload "${item.file.name}". ${message}`);
          setSubmitting(false);
          return;
        }
      }

      const job = await createJob({
        user_id: selectedUser.id,
        splitter_key: splitter.key,
        options: optionValues,
        inputs: uploaded,
      }).unwrap();

      const label = `${selectedUser.username} · ${splitter.label}`;
      dispatch(trackPdfSplitJob({ jobId: job.job_id, label }));
      dispatch(
        pushNotification({
          message: `Split job started for ${selectedUser.username}.`,
          severity: "success",
        }),
      );
      resetForm();
      onClose();
      navigate(`/splitting-tools/${job.job_id}`);
    } catch (err: unknown) {
      setFormError(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not start the split job. Please try again.",
        ),
      );
      setSubmitting(false);
    }
  };

  const totalToUpload = items.length;

  const renderOptionField = (
    name: string,
    property: PdfOptionSchemaProperty,
  ) => {
    const label = property.title || name;
    const required = requiredOptions.includes(name);
    if (property.type === "boolean") {
      return (
        <Box key={name}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(optionValues[name])}
                onChange={(e) => setOption(name, e.target.checked)}
                disabled={submitting}
              />
            }
            label={label}
          />
          {property.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: -0.5 }}
            >
              {property.description}
            </Typography>
          )}
        </Box>
      );
    }

    const isSecret = property.format === "password" || property.writeOnly;
    return (
      <TextField
        key={name}
        size="small"
        label={required ? `${label} *` : label}
        type={isSecret && !showSecret[name] ? "password" : "text"}
        value={String(optionValues[name] ?? "")}
        onChange={(e) => setOption(name, e.target.value)}
        disabled={submitting}
        helperText={property.description}
        autoComplete={isSecret ? "new-password" : undefined}
        InputProps={
          isSecret
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setShowSecret((prev) => ({
                          ...prev,
                          [name]: !prev[name],
                        }))
                      }
                      aria-label={`Toggle ${label} visibility`}
                    >
                      {showSecret[name] ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    );
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="New Split Job"
      maxWidth="sm"
      showCloseIcon={!submitting}
      actions={
        <>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {submitting ? "Starting…" : "Start Splitting"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {configLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Autocomplete
              options={userOptions}
              loading={usersLoading}
              componentsProps={backendUserAutocompleteComponentsProps}
              ListboxComponent={BackendUserAutocompleteListbox}
              ListboxProps={{ onScroll: usersListboxOnScroll }}
              value={selectedUser}
              onChange={(_e, v) => setSelectedUser(v)}
              getOptionLabel={formatBackendUserOptionLabel}
              renderOption={renderBackendUserAutocompleteOption}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={submitting}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client User *"
                  onChange={(e) => setUserSearch(e.target.value)}
                  helperText="The user whose Data Storage will receive the saved result."
                />
              )}
            />

            <TextField
              select
              size="small"
              label="Splitter *"
              value={splitterKey}
              onChange={(e) => handleSplitterChange(e.target.value)}
              disabled={submitting}
              helperText="The document format to split. Each splitter knows its own options."
            >
              {splitters.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>

            {schemaProperties.map(([name, property]) =>
              renderOptionField(name, property),
            )}

            <PdfFilesDropzone
              items={items}
              onAddFiles={handleAddFiles}
              onRemove={handleRemoveFile}
              disabled={submitting}
            />

            {submitting && totalToUpload > 0 && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (uploadedCount / totalToUpload) * 100)}
                  sx={{ borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Uploading file {Math.min(uploadedCount + 1, totalToUpload)} of{" "}
                  {totalToUpload}…
                </Typography>
              </Box>
            )}

            {formError && <Alert severity="error">{formError}</Alert>}
          </>
        )}
      </Box>
    </AppDialog>
  );
}
